import type { FC, ReactNode } from 'react'

import { Check, Mail, MapPin, Phone } from 'lucide-react'
import { ContactForm } from '@/components/landing/contact-form'
import { SectionHeading } from '@/components/landing/section-heading'
import { company } from '@/constants/company'
import { CONSULTATION_TOPICS } from '@/constants/content'

const contactCard = 'flex items-start gap-4 rounded-2xl border border-border bg-card p-4 transition-colors'

export const ContactSection: FC = (): ReactNode => {
    return (
        <section
            id='kontakt'
            aria-labelledby='kontakt-title'
            className='relative overflow-hidden py-24 sm:py-28'
        >
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_80%_60%_at_80%_20%,black_20%,transparent_75%)]'
            />

            <div className='relative mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-12 lg:gap-16'>
                <div className='lg:col-span-5'>
                    <SectionHeading
                        titleId='kontakt-title'
                        eyebrow='Kontakt'
                        title='Projekt unverbindlich besprechen.'
                        description='Erzählen Sie uns kurz, welches Bauteil ansteht und wann betoniert wird. Wir melden uns persönlich und vereinbaren ein unverbindliches Erstgespräch.'
                    />

                    <div className='mt-10'>
                        <p className='font-mono text-[0.68rem] font-medium uppercase tracking-[0.18em] text-muted-foreground'>
                            Im Erstgespräch klären wir
                        </p>

                        <ul className='mt-4 grid gap-3'>
                            {CONSULTATION_TOPICS.map((topic) => (
                                <li key={topic} className='flex gap-3 text-foreground/85'>
                                    <Check className='mt-1 size-4 shrink-0 text-primary' aria-hidden='true' />
                                    {topic}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <ul className='mt-10 grid gap-3'>
                        <li>
                            <a href={company.phoneHref} className={`${contactCard} hover:border-primary/40`}>
                                <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground'>
                                    <Phone className='size-4' aria-hidden='true' />
                                </span>

                                <span>
                                    <span className='block text-sm text-muted-foreground'>Telefon</span>
                                    <span className='block font-medium text-foreground'>{company.phone}</span>
                                </span>
                            </a>
                        </li>

                        <li>
                            <a href={`mailto:${company.email}`} className={`${contactCard} hover:border-primary/40`}>
                                <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground'>
                                    <Mail className='size-4' aria-hidden='true' />
                                </span>

                                <span className='min-w-0'>
                                    <span className='block text-sm text-muted-foreground'>E-Mail</span>
                                    <span className='block break-all font-medium text-foreground'>{company.email}</span>
                                </span>
                            </a>
                        </li>

                        <li className={contactCard}>
                            <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground'>
                                <MapPin className='size-4' aria-hidden='true' />
                            </span>

                            <span>
                                <span className='block text-sm text-muted-foreground'>Einsatzgebiet: {company.serviceArea}</span>

                                <address className='mt-0.5 block font-medium not-italic leading-relaxed text-foreground'>
                                    ConcreTrack · {company.legalName}
                                    <br />
                                    {company.street}, {company.postalCode} {company.city}
                                </address>
                            </span>
                        </li>
                    </ul>
                </div>

                <div className='lg:col-span-7'>
                    <div className='rounded-[1.75rem] border border-border bg-card p-6 shadow-[0_1px_2px_rgb(18_28_26/0.04),0_32px_64px_-36px_rgb(18_28_26/0.3)] sm:p-9'>
                        <h3 className='text-xl font-medium text-foreground'>
                            Ihre Projektanfrage
                        </h3>

                        <p className='mt-1.5 text-[0.95rem] text-muted-foreground'>
                            Pflichtfelder sind Name, E-Mail und eine kurze Beschreibung.
                        </p>

                        <div className='mt-8'>
                            <ContactForm />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
